import { Button, cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Trash2, GripVertical, Home, ChevronDown, Plus } from "lucide-react";
import type { Page } from "../../../shared/schema";

export interface PageItemProps {
  page: Page;
  isFirst: boolean;
  isSelected: boolean;
  isDragging: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  /** Whether this page's sub-pages are currently shown */
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** Adds a new sub-page under this page */
  onAddChildPage: () => void;
}

// A top-level page row: supports an expand/collapse chevron for its sub-pages and an "Add sub-page" affordance.
export const PageItem: React.FC<PageItemProps> = ({
  page,
  isFirst,
  isSelected,
  isDragging,
  canDelete,
  onSelect,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnd,
  isExpanded,
  onToggleExpanded,
  onAddChildPage,
}) => {
  const { t } = useTranslation();
  const hasChildren = page.pages.length > 0;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={cn(
        "group flex items-center gap-1 rounded-md px-2 py-2 text-sm",
        isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted",
        isDragging && "opacity-50"
      )}
    >
      <button
        type="button"
        className={cn(
          "p-0.5 rounded flex-shrink-0",
          hasChildren ? "hover:bg-muted/50" : "invisible",
          isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
        )}
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpanded();
        }}
        title={isExpanded ? t("Collapse sub-pages") : t("Expand sub-pages")}
      >
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isExpanded ? "rotate-180" : "")} />
      </button>

      <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted/50 rounded flex-shrink-0">
        <GripVertical
          className={cn("h-3.5 w-3.5", isSelected ? "text-primary-foreground/70" : "text-muted-foreground")}
        />
      </div>

      <div className="flex-1 flex items-center gap-2 cursor-pointer min-w-0" onClick={onSelect}>
        {isFirst && (
          <span title={t("Home page")}>
            <Home
              className={cn(
                "h-3 w-3 flex-shrink-0",
                isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
              )}
            />
          </span>
        )}
        <span className="truncate">{page.title}</span>
        {page.visibility && (
          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded flex-shrink-0">
            {t("Conditional")}
          </span>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "h-6 w-6 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0",
          isSelected ? "hover:bg-primary-foreground/10 text-primary-foreground" : "hover:bg-muted-foreground/10"
        )}
        onClick={(e) => {
          e.stopPropagation();
          onAddChildPage();
        }}
        title={t("Add sub-page")}
      >
        <Plus className="h-3 w-3" />
      </Button>

      {canDelete && (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 w-6 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0",
            isSelected
              ? "hover:bg-primary-foreground/10 text-primary-foreground"
              : "hover:bg-destructive/10 hover:text-destructive"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};
